import React from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import SecurityIcon from '@mui/icons-material/Security';
import LanguageIcon from '@mui/icons-material/Language';
import ConstructionIcon from '@mui/icons-material/Construction';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const settingsCategories = [
    {
      title: 'Account Settings',
      icon: <AccountCircleIcon />,
      description: 'Manage your profile, email, and account preferences',
      status: 'Coming Soon'
    },
    {
      title: 'Notification Preferences',
      icon: <NotificationsIcon />,
      description: 'Control trade alerts, waiver notifications, and league updates',
      status: 'In Development'
    },
    {
      title: 'Theme & Display',
      icon: <ColorLensIcon />,
      description: 'Dark mode, color themes, and display customization',
      status: 'Planned'
    },
    {
      title: 'Privacy & Security',
      icon: <SecurityIcon />,
      description: 'Password, two-factor authentication, and privacy settings',
      status: 'Coming Soon'
    },
    {
      title: 'Language & Region',
      icon: <LanguageIcon />,
      description: 'Language preferences and timezone settings',
      status: 'Future Release'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Development': return 'warning';
      case 'Coming Soon': return 'info';
      case 'Planned': return 'default';
      case 'Future Release': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <ConstructionIcon sx={{ fontSize: 48, color: 'warning.main', mr: 2 }} />
          <SettingsIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          This page is currently under development
        </Typography>
        <Chip 
          label="🚧 Work in Progress" 
          color="warning" 
          variant="outlined"
          sx={{ fontSize: '1rem', py: 2 }}
        />
      </Paper>

      {/* Planned Features */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            Planned Settings Categories
          </Typography>
          <List>
            {settingsCategories.map((category, index) => (
              <React.Fragment key={category.title}>
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon sx={{ minWidth: 56 }}>
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{category.title}</Typography>
                        <Chip 
                          label={category.status} 
                          size="small"
                          color={getStatusColor(category.status) as any}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={category.description}
                  />
                </ListItem>
                {index < settingsCategories.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Development Info */}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h3" gutterBottom>
            What's Coming?
          </Typography>
          <Typography variant="body1" paragraph>
            We're actively working on building a comprehensive settings experience that will allow you to:
          </Typography>
          <List dense>
            <ListItem>
              <Typography variant="body2">• Customize your notification preferences for trades, waivers, and league events</Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">• Choose between light and dark themes</Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">• Manage your account security and privacy settings</Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">• Configure display preferences and accessibility options</Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">• Set your timezone and regional preferences</Typography>
            </ListItem>
          </List>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              💡 <strong>Have suggestions?</strong> We'd love to hear what settings are most important to you! 
              Your feedback helps us prioritize which features to build first.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SettingsPage;
